import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    city: '',
    bill: '',
    consent: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send to backend API
      await axios.post(`${API}/leads`, formData);

      // Track Meta Pixel Lead Conversion
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Solar Lead Form',
          content_category: 'Solar Inquiry',
          value: formData.bill,
          currency: 'INR'
        });
      }

      toast({
        title: "Form Submitted Successfully!",
        description: "Our team will contact you within 2 hours.",
        duration: 5000,
      });

      // Reset form
      setFormData({
        name: '',
        mobile: '',
        city: '',
        bill: '',
        consent: false
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Submission Failed",
        description: "Please try again or call us directly.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-orange-500 to-yellow-400 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-900/20 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold leading-tight">
              Don't Miss Out on ₹1,38,000 Subsidy!
            </h2>
            <p className="text-2xl text-orange-100 font-medium">
              Limited slots available. Government subsidy ending soon.
            </p>

            <div className="space-y-4 pt-6">
              {[
                'Free site survey within 24 hours',
                'Zero-cost subsidy paperwork',
                'Installation in 7-10 days',
                'Start saving immediately'
              ].map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-300 flex-shrink-0" />
                  <span className="text-lg text-white font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Urgency Timer */}
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 mt-8">
              <p className="text-white font-bold text-lg mb-2">⚡ Special Offer Valid Today Only</p>
              <p className="text-orange-100">Get extra 5% discount for applications received today</p>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-3xl p-6 md:p-10 shadow-2xl">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Get Your Solar Savings & Subsidy Estimate
            </h3>
            <p className="text-gray-600 mb-6 md:mb-8">Takes less than 60 seconds</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2" htmlFor="name">
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring focus:ring-orange-200"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2" htmlFor="mobile">
                  Mobile Number *
                </label>
                <Input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  required
                  pattern="[0-9]{10}"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="10-digit mobile number"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring focus:ring-orange-200"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2" htmlFor="city">
                  City *
                </label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  required
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Your city in Odisha"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring focus:ring-orange-200"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2" htmlFor="bill">
                  Monthly Electricity Bill *
                </label>
                <Input
                  id="bill"
                  name="bill"
                  type="text"
                  required
                  value={formData.bill}
                  onChange={handleChange}
                  placeholder="e.g., ₹5000"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring focus:ring-orange-200"
                />
              </div>

              {/* Consent Checkbox */}
              <div className="flex items-start space-x-3 bg-orange-50 p-4 rounded-xl border border-orange-200">
                <input
                  id="consent"
                  name="consent"
                  type="checkbox"
                  required
                  checked={formData.consent}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500 focus:ring-2 cursor-pointer"
                />
                <label htmlFor="consent" className="text-sm text-gray-700 cursor-pointer">
                  <span className="font-semibold">I agree</span> to receive promotional & marketing calls, emails, sms, and WhatsApp messages from <span className="font-semibold text-orange-600">BIJAYA SOLAR</span> for marketing and promotional purposes. *
                </label>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-yellow-400 hover:from-orange-600 hover:to-yellow-500 text-white text-base md:text-lg font-bold py-5 md:py-6 rounded-xl shadow-2xl"
              >
                <span className="hidden sm:inline">Get Your Solar Savings & Subsidy Estimate</span>
                <span className="sm:hidden">Get Your Estimate</span>
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              <p className="text-center text-sm text-gray-500">
                🔒 Your information is 100% secure and confidential
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadForm;